/// <reference path="../../nativewind-augmentation.d.ts" />
// Copyright (c) Ping. All rights reserved.
//
// Dispatcher for the in-chat game tiles. Renders the live, animated,
// interactive tile for a game session by kind. The host supplies the live
// `session` (folded from `useGameSessions` by `sessionId`) + an `onMove`
// that emits `com.ping.game_move`. Coin Flip / Dice / RPS are fully
// playable; the rest show a polished "coming soon" card.

import { Car, Castle, Gamepad2, type LucideIcon, Spade } from 'lucide-react-native';
import { Text, View } from 'react-native';
import { useColorScheme } from '../../foundations/colorScheme';
import { tokens } from '../../design-system/tokens';
import { useTileMaxWidth } from '../tileConstraint';
import { CoinFlipTile } from './CoinFlipTile';
import { DiceRollTile } from './DiceRollTile';
import { RpsTile } from './RpsTile';
import { Header } from './CoinFlipTile';
import type { GameKind } from '../../appEvent/types';
import type { GameSession } from '../../sync/useGameSessions';

export interface GameTileProps {
  /** Variant's declared game kind (fallback before the session folds). */
  readonly gameKind: GameKind;
  readonly sessionId: string;
  /** Was the `game_create` mine? (the create message's `isSelf`). */
  readonly isCreator: boolean;
  /** Live folded session (from the host's `useGameSessions`). */
  readonly session?: GameSession | undefined;
  /** Emit a move: `com.ping.game_move` with this move index + payload. */
  readonly onMove?: ((sessionId: string, moveIdx: number, payload: string) => void) | undefined;
  readonly selfName?: string | undefined;
  readonly opponentName?: string | undefined;
  /**
   * Compact/stacked layout. In compact mode the tile keeps the neutral
   * surface background (no self-brand fill); only bubble mode tints a
   * self-created game with the indigo brand.
   */
  readonly compact?: boolean | undefined;
}

const COMING_SOON: Record<string, { Icon: LucideIcon; title: string }> = {
  chess: { Icon: Castle, title: 'Chess' },
  blackjack: { Icon: Spade, title: 'Blackjack' },
  racing: { Icon: Car, title: 'Racing' },
};

export function GameTile({
  gameKind,
  sessionId,
  isCreator,
  session,
  onMove,
  selfName,
  opponentName,
  compact,
}: GameTileProps): JSX.Element {
  // Self-created games tint with the indigo brand ONLY in bubble mode;
  // compact rows stay on the neutral surface.
  const onBrand = isCreator && compact !== true;
  // Dispatch on the VARIANT's kind (from the authoritative `game_create`
  // message), not the folded session — a session synthesized from a
  // self-move can carry the reducer's default kind. State is read from
  // `session.moves`, which is always correct.
  const kind = gameKind;
  const move = (moveIdx: number, payload: string): void =>
    onMove?.(sessionId, moveIdx, payload);

  // Until the session folds (the create envelope is always applied first,
  // so this is a brief window), render a lightweight placeholder session.
  const live: GameSession =
    session ??
    ({
      sessionId,
      gameKind: kind,
      phase: 'open',
      createdBy: '',
      moves: [],
    } as GameSession);

  if (kind === 'coinflip') {
    return (
      <CoinFlipTile
        session={live}
        isCreator={isCreator}
        onBrand={onBrand}
        onMove={move}
        selfName={selfName}
        opponentName={opponentName}
      />
    );
  }
  if (kind === 'dice') {
    return (
      <DiceRollTile
        session={live}
        isCreator={isCreator}
        onBrand={onBrand}
        onMove={move}
        selfName={selfName}
        opponentName={opponentName}
      />
    );
  }
  if (kind === 'rps') {
    return (
      <RpsTile
        session={live}
        isCreator={isCreator}
        onBrand={onBrand}
        onMove={move}
        selfName={selfName}
        opponentName={opponentName}
      />
    );
  }
  return <ComingSoonTile kind={kind} onBrand={onBrand} />;
}

function ComingSoonTile({ kind, onBrand }: { kind: GameKind; onBrand: boolean }): JSX.Element {
  const scheme = useColorScheme();
  const c = tokens.color.semantic[scheme === 'dark' ? 'dark' : 'light'];
  // Thread side panel: cap the tile to the provided width.
  const tileMax = useTileMaxWidth();
  const meta = COMING_SOON[kind] ?? { Icon: Gamepad2, title: 'Game' };
  return (
    <View
      className={`w-[280px] max-w-full rounded-[18px] p-md gap-xs border ${
        onBrand
          ? 'bg-light-accent-brand dark:bg-[#537EFF] border-white/10'
          : 'bg-light-surface-secondary dark:bg-[#1A1D24] border-light-border-secondary dark:border-white/[0.06]'
      }`}
      style={tileMax !== null ? { maxWidth: tileMax } : undefined}
    >
      <Header title={meta.title} Icon={meta.Icon} colorAccent={c.accent.indigo} />
      <Text className="font-sans text-[12px] leading-4 text-light-text-tertiary dark:text-[#B5BAC3]">
        {meta.title} is coming soon — Coin Flip, Dice Roll and Rock Paper Scissors are ready to play now.
      </Text>
    </View>
  );
}
